<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import type {
  DashboardRunOption,
  DashboardSectionKey,
} from "../../features/dashboard/dashboard.types.ts";

// Title/subtitle moved to AppHeader (driven by route meta); this bar now owns
// only the run selector and section filters.
const props = defineProps<{
  lastRunLabel: string;
  runOptions: DashboardRunOption[];
  selectedRunId: string;
  selectedSectionKeys: DashboardSectionKey[];
  sectionLabels: Record<DashboardSectionKey, string>;
  filterSummary: string;
}>();

const emit = defineEmits<{
  (event: "update:selectedRunId", value: string): void;
  (event: "toggle-section", value: DashboardSectionKey): void;
}>();

const filtersOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);
const sectionKeys = computed(
  () => Object.keys(props.sectionLabels) as DashboardSectionKey[]
);

function toggleFilters(): void {
  filtersOpen.value = !filtersOpen.value;
}

function onDocumentClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Node)) {
    return;
  }
  if (!menuRef.value?.contains(target)) {
    filtersOpen.value = false;
  }
}

function onEscape(event: KeyboardEvent): void {
  if (event.key === "Escape") {
    filtersOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
  document.addEventListener("keydown", onEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
  document.removeEventListener("keydown", onEscape);
});
</script>

<template>
  <div
    class="dashboard-card dashboard-card--strong flex flex-col gap-4 rounded-[1.5rem] px-5 py-4 lg:flex-row lg:items-start lg:justify-end"
  >
    <div class="flex flex-col gap-3 lg:items-end">
      <div
        class="dashboard-text-secondary flex flex-wrap items-center gap-3 text-sm"
      >
        <span class="dashboard-control rounded-full px-3 py-2">
          Run Date: {{ lastRunLabel }}
        </span>
        <select
          id="dashboard-run-select"
          :value="selectedRunId"
          aria-label="Redistribution run"
          class="dashboard-control rounded-xl px-4 py-2 text-sm font-medium transition"
          @change="
            emit(
              'update:selectedRunId',
              ($event.target as HTMLSelectElement).value
            )
          "
        >
          <option
            v-for="run in runOptions"
            :key="run.id"
            :value="run.id"
            class="dashboard-text-primary"
          >
            {{ run.label }}
          </option>
        </select>

        <div ref="menuRef" class="relative">
          <button
            type="button"
            class="dashboard-control rounded-xl px-4 py-2 text-sm font-medium transition"
            :aria-expanded="filtersOpen"
            aria-haspopup="menu"
            @click.stop="toggleFilters"
          >
            Filters
          </button>

          <div
            v-if="filtersOpen"
            class="dashboard-menu-panel absolute right-0 z-20 mt-2 w-72 rounded-2xl p-4"
            role="menu"
          >
            <p
              class="dashboard-text-muted text-xs font-semibold tracking-[0.18em] uppercase"
            >
              Dashboard sections
            </p>
            <p class="dashboard-text-secondary mt-2 text-xs">
              {{ filterSummary }}
            </p>

            <div class="mt-4 grid gap-2">
              <label
                v-for="sectionKey in sectionKeys"
                :key="sectionKey"
                :for="`dashboard-section-${sectionKey}`"
                class="dashboard-filter-option flex items-center gap-3 rounded-xl px-3 py-2 text-sm"
              >
                <input
                  :id="`dashboard-section-${sectionKey}`"
                  :checked="selectedSectionKeys.includes(sectionKey)"
                  class="h-4 w-4 rounded border-white/20 bg-transparent accent-[#3b82f6]"
                  type="checkbox"
                  @change="emit('toggle-section', sectionKey)"
                />
                <span>{{ sectionLabels[sectionKey] }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
      <p class="dashboard-text-muted text-xs">{{ filterSummary }}</p>
    </div>
  </div>
</template>
